// frontend/src/lib/attendeeReturnCredential.ts
import { isNativeApp } from './native/platform'
import { clearCredential, getCredential, setCredential } from './native/credentialStore'

const RETURN_KEY = 'attendee_return'
const MAX = 5

/**
 * [LW-24 Slice 5, Tier 2] Device-local re-entry credentials for lobby-day attendees. Holds each
 * attendee's working magic-link TOKEN + its event name, so a "Welcome back" card on the attendee
 * dead-end can deep-link back to a still-good schedule. Kind-namespaced under its own key
 * (`attendee_return`) so it never clobbers the canvasser `canvasser_return` on a dual-role device;
 * event-isolated because each attendee-per-event token is unique. Holds NO personal name (the grant
 * mirror stays ids-only; a borrowed device must not flash the previous person's name). eventName is a
 * public event label shown only for disambiguation. Device-only, never synced to the server.
 *
 * [Native B1, NB1-12] On native the same list lives in OS secure storage (credentialStore kind
 * 'attendeeReturn', namespaced by instance), never localStorage. Web is unchanged.
 */
export interface AttendeeReturn {
  token: string
  eventName: string
}

function isEntry(v: unknown): v is AttendeeReturn {
  return (
    typeof v === 'object' && v !== null &&
    typeof (v as AttendeeReturn).token === 'string' &&
    typeof (v as AttendeeReturn).eventName === 'string'
  )
}

export function getAttendeeReturns(): AttendeeReturn[] {
  const raw = isNativeApp() ? getCredential('attendeeReturn') : localStorage.getItem(RETURN_KEY)
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    const clean = parsed.filter(isEntry).map((c) => ({ token: c.token, eventName: c.eventName }))
    return clean.length === parsed.length ? clean : []
  } catch {
    return []
  }
}

export function rememberAttendeeReturn(entry: AttendeeReturn): void {
  try {
    const rest = getAttendeeReturns().filter((c) => c.token !== entry.token)
    const next = [{ token: entry.token, eventName: entry.eventName }, ...rest].slice(0, MAX)
    const value = JSON.stringify(next)
    // Native: async secure-storage write; a failure is swallowed exactly like a web quota error.
    if (isNativeApp()) void setCredential('attendeeReturn', value).catch(() => {})
    else localStorage.setItem(RETURN_KEY, value)
  } catch {
    // Private mode / quota — re-entry just won't be available on this device. Never strand the caller.
  }
}

export function clearAttendeeReturns(): void {
  // Native: the in-memory copy is dropped synchronously, so the wipe is immediately visible.
  if (isNativeApp()) {
    void clearCredential('attendeeReturn').catch(() => {})
    return
  }
  localStorage.removeItem(RETURN_KEY)
}
